import { ApiError } from "./error";

import type { Endpoint, Input, Output } from "./codegen-rest";

export type SendRestOptions<E extends Endpoint> = {
  endpoint: E;
  signal?: AbortSignal;
} & Input<E>;

type ApiSuccess<T> = {
  success: true;
  data: T;
};

type ApiFailure = {
  success: false;
  data: {
    code: number;
    message: string;
  };
};

export type RestApiClientResponse<E extends Endpoint> = ApiSuccess<Output<E>> | ApiFailure;
export type RestApiClientSuccessResponse<E extends Endpoint> = ApiSuccess<Output<E>>;

export class RestApiClient {
  private static BASE_URL = window.location.origin + "/api/v1";

  async send<E extends Endpoint>({
    endpoint,
    params,
    query,
    body,
    signal,
  }: SendRestOptions<E>): Promise<Output<E>> {
    const [method, path] = endpoint.split(" ");

    if (!method || !path) {
      throw new Error(`Invalid endpoint: ${endpoint}`);
    }

    const processedPath = this.setParams(path, params);
    const url = new URL(processedPath, RestApiClient.BASE_URL);

    this.setQuery(url, query);

    const request = new Request(url, {
      method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });

    try {
      const response = await fetch(request);

      try {
        const parsedResponse: RestApiClientResponse<E> = await response.json();

        if (Object.hasOwn(parsedResponse, "cloudflare_error")) {
          throw new ApiError(response.status, response.statusText);
        }

        const { success, data } = parsedResponse;

        if (!success) throw new ApiError(data.code, data.message);

        return data;
      } catch (error) {
        if (error instanceof ApiError) throw error;

        throw new Error(String(error), { cause: error });
      }
    } catch (error) {
      if (error instanceof ApiError) throw error;

      throw new Error(String(error), { cause: error });
    }
  }

  private setParams(path: string, params?: Record<string, unknown>): string {
    let result = path;
    Object.entries(params ?? {}).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{${key}\\}`, "g"), encodeURIComponent(String(value)));
    });
    return result;
  }

  private setQuery(url: URL, query?: Record<string, unknown>) {
    Object.entries(query ?? {}).forEach(([key, value]) => {
      if (value === undefined) {
        url.searchParams.delete(key);
      } else if (Array.isArray(value)) {
        url.searchParams.delete(key);
        value.forEach((item) => url.searchParams.append(key, String(item)));
      } else {
        url.searchParams.set(key, String(value));
      }
    });
  }
}

export const restApiClient = new RestApiClient();
